import _ from 'lodash';
import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Icon from './Icon';
import Title from './Title';
import List from './List';
import ListItem from './ListItem';
import ListLinkItem from './ListLinkItem';
import BackButton from './BackButton';
import ListToggleItem from './ListToggleItem';
import { getString as s } from '../lib/strings';
import { getShow as show } from '../lib/show';
import copyRef from '../lib/copyRef';
import { ConfirmModal, NoticeModal } from './Modal';
import Layout, { LayoutHeader, LayoutBody } from './Layout';
import Banner from './Banner';
import { toast } from './Toast';
import Loader from './Loader';

const ProfilingText = styled.div`
  ${(props) => props.theme.type.base} font-size: 12px;
`;

const PermissionsLayout = styled(Layout)`
  .permissions-list-toggle h3 {
    font-family: ${props => props.theme.type.h3}
  }
`;

class Permissions extends Component {
  /*
  Note to future devs:
  This is a quick and dirty way to get these modals going (time pressure).
  This should all be swapped out for an app level notification system (toats, modals etc...)
  driven centrally via redux.
  There is a nice pattern suggested by Abramov on SO.
  - Alex.
   */
  state = {
    modal: false
  };
  componentDidMount() {
    const { fetchPermissions, getNewsletterUri, brand } = this.props;
    fetchPermissions();
    if (brand === 'wls') {
      getNewsletterUri();
    }
  }

  listAllowedChannels = (channels) => {
    const allowed = _(channels)
      .reduce((result, value, key) => {
        if (value) {
          result.push(key);
        }
        return result;
      }, [])
      .sort((a, b) => s(`channel.${a}.order`, -1) - s(`channel.${b}.order`, -1))
      .map((value) => s(`channel.${value}.displayName`, value).toLowerCase());

    allowed[0] = _.capitalize(allowed[0]);
    return allowed;
  };

  toggleAllChannels = (currentValue) => {
    const { product, setAllPermissions, userPermission } = this.props;
    if (currentValue) {
      this.setState({ modal: 'confirm' });
    } else {
      this.setState({ modal: 'inform' });
      setAllPermissions(product, true, userPermission);
    }
  };

  toggleChannel = (permission, currentValue) => {
    const { setPermission, userPermission } = this.props;
    setPermission(permission, !currentValue, userPermission);
  };

  closeModals = () => this.setState({ modal: false });

  render() {

    const {
      product,
      permissions,
      newsletterUri,
      history,
      setAllPermissions,
      userPermission,
      isFetching,
      allAllowed,
      setPermission // eslint-disable-line
    } = this.props;

    const showSingleChannelToggle = show('permissions.singleChannelToggle', false);

    if (this.props.error) {
      const title = s('channel.error.title', 'Sorry!');
      const body = s(
        'channel.error.subtitle',
        'There was a problem. Please refresh your browser.'
      );
      toast.error(title, body);
    }

    return (
      <PermissionsLayout>
        {!allAllowed && (
          <Banner
            primary={copyRef(product, 'opt_out_confirmation')}
            secondary="You can manage your permissions below"
          />
        )}
        {isFetching && <Loader isLoading={isFetching} />}
        {!isFetching && (
          <LayoutHeader className="layout-header">
            <BackButton brand={this.props.brand} onClick={() => history.push('/products/')}>
              {s('permission.back', 'Go Back')}
            </BackButton>
          </LayoutHeader>
        )}
        {!isFetching && (
          <LayoutBody brand={this.props.brand}>
            <Title
              brand={this.props.brand}
              primary={copyRef(product, 31)}
              secondary={copyRef(product, 'permission_description')}
            />
            <List id="permissions_list">
              {permissions.map((permission) => (showSingleChannelToggle && permission.channels && permission.channels.length === 1) ? (
                <ListToggleItem
                  brand={this.props.brand}
                  key={`perm-${permission.id}`}
                  primary={copyRef(permission, 'name')}
                  secondary={copyRef(permission, 'channel_description')}
                  checked={permission.allowed[permission.channels.slice(0, 1)]}
                  onClick={() =>
                    this.toggleChannel(
                      permission,
                      permission.allowed[permission.channels.slice(0, 1)]
                    )
                  }
                />
              ) : (
                <ListItem
                  key={`perm-${permission.id}`}
                  primary={copyRef(permission, 'name')}
                  secondary={this.listAllowedChannels(permission.allowed).join(', ') || 'None'}
                  addon={<Icon brand={this.props.brand} name="rightChevron" />}
                  onClick={() =>
                    history.push(`/products/${product.id}/${permission.id}`)
                  }
                  button
                />
              )
              )}
            </List>

            {this.props.brand === 'wls' && product.code === 'NEWS_BROADCASTING_GENERAL_PERMISSION' && userPermission && (
              <ListLinkItem
                brand={this.props.brand}
                primary={s('product.newsletter.title', 'Newsletters')}
                secondary={s('product.newsletter.subtitle', 'Desc')}
                url={newsletterUri.uri}
                urlText={s('product.newsletter.link', 'link')}
              />
            )}

            <ListToggleItem
              brand={this.props.brand}
              className="permissions-list-toggle"
              id="product_opt_toggle"
              primary={copyRef(product, 35)}
              secondary={copyRef(product, 'usp_cta')}
              checked={allAllowed}
              name={`toggle ${copyRef(product, 35)}`}
              onClick={() => this.toggleAllChannels(allAllowed)}
            />

            <ConfirmModal
              brand={this.props.brand}
              isOpen={this.state.modal === 'confirm'}
              onClose={this.closeModals}
              onConfirm={() => setAllPermissions(product, false, userPermission)}
              title={copyRef(product, 'opt_out_warning_title')}
              text={copyRef(product, 'opt_out_warning')}
              notice={s(
                'permission.optOut.confirm.notice',
                'Changes may take a few days to take effect'
              )}
              confirmLabel={s('permission.optOut.confirm.yes', 'Yes')}
              cancelLabel={s('permission.optOut.confirm.no', 'No')}
            />
            {!this.props.error && (
              <NoticeModal
                isOpen={this.state.modal === 'inform'}
                onClose={this.closeModals}
                text={copyRef(product, 'opt_in_confirmation')}
              />
            )}
            <ProfilingText
              dangerouslySetInnerHTML={{ __html: copyRef(product, 'profiling_notice', false) }}
            />
          </LayoutBody>
        )}
      </PermissionsLayout>
    );
  }
}

Permissions.propTypes = {
  product: PropTypes.shape({
    allowed: PropTypes.object,
    displayName: PropTypes.string,
    id: PropTypes.string,
    code: PropTypes.string,
    version: PropTypes.string
  }).isRequired,
  userPermission: PropTypes.shape({
    id: PropTypes.string
  }).isRequired,
  allAllowed: PropTypes.bool.isRequired,
  newsletterUri: PropTypes.shape({
    uri: PropTypes.string
  }),
  getNewsletterUri: PropTypes.func,
  permissions: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchPermissions: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  setAllPermissions: PropTypes.func.isRequired,
  error: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  setPermission: PropTypes.func.isRequired,
  brand: PropTypes.string.isRequired
};

export default Permissions;
